<template>
	<div>
		<div class="row no-gutters" style="gap: .4rem 0">
			<div class="col-6 pr-1"><label class="small mb-0">X</label><b-form-input size="sm" type="number" v-model.number="layer.x" @change="$emit('save')" /></div>
			<div class="col-6 pl-1"><label class="small mb-0">Y</label><b-form-input size="sm" type="number" v-model.number="layer.y" @change="$emit('save')" /></div>
		</div>
		<div class="d-flex align-items-end mt-2" style="gap: .4rem">
			<div class="flex-fill"><label class="small mb-0">Width</label><b-form-input size="sm" type="number" min="1" v-model.number="layer.w" @change="$emit('save')" /></div>
			<div class="flex-fill"><label class="small mb-0">Height</label><b-form-input size="sm" type="number" min="1" v-model.number="layer.h" @change="$emit('save')" /></div>
			<b-button v-if="cropable" size="sm" variant="secondary" title="Reset size" @click="resetSize"><b-icon icon="bootstrap-reboot" /></b-button>
		</div>
		<div class="d-flex align-items-end mt-2" style="gap: .4rem">
			<div class="flex-fill"><label class="small mb-0">Rotation</label><b-form-input size="sm" type="number" v-model.number="layer.rotation" @change="$emit('save')" /></div>
			<b-button size="sm" variant="secondary" title="Reset rotation" @click="resetRotation"><b-icon icon="bootstrap-reboot" /></b-button>
		</div>
		<template v-if="cropable">
			<label class="small text-muted mb-0 mt-2 d-block">Crop</label>
			<div class="row no-gutters" style="gap: .4rem 0">
				<div class="col-6 pr-1"><label class="small mb-0">Left</label><b-form-input size="sm" type="number" min="0" :value="round(insets.left)" @change="setInset('left', $event)" /></div>
				<div class="col-6 pl-1"><label class="small mb-0">Top</label><b-form-input size="sm" type="number" min="0" :value="round(insets.top)" @change="setInset('top', $event)" /></div>
			</div>
			<div class="d-flex align-items-end mt-2" style="gap: .4rem">
				<div class="flex-fill"><label class="small mb-0">Right</label><b-form-input size="sm" type="number" min="0" :value="round(insets.right)" @change="setInset('right', $event)" /></div>
				<div class="flex-fill"><label class="small mb-0">Bottom</label><b-form-input size="sm" type="number" min="0" :value="round(insets.bottom)" @change="setInset('bottom', $event)" /></div>
				<b-button size="sm" variant="secondary" title="Reset crop" @click="resetCrop"><b-icon icon="bootstrap-reboot" /></b-button>
			</div>
		</template>
		<div class="d-flex mt-2" style="gap: .35rem">
			<b-button size="sm" variant="secondary" class="flex-fill" title="Center horizontally" @click="center('x')"><b-icon icon="distribute-horizontal" /></b-button>
			<b-button size="sm" variant="secondary" class="flex-fill" title="Center vertically" @click="center('y')"><b-icon icon="distribute-vertical" /></b-button>
			<b-button size="sm" variant="secondary" class="flex-fill" title="Fill the screen" @click="fill"><b-icon icon="fullscreen" /></b-button>
		</div>
	</div>
</template>

<script>
import { naturalSize, cropInsets, setCropInset } from '@/overlayCrop';
import { isMediaLayer } from './layerTypes';

export default {
	props: {
		layer: { type: Object, required: true },
		overlay: { type: Object, required: true },
		// src -> [naturalWidth, naturalHeight], as loaded (and cached) by the editor.
		mediaSizes: { type: Object, default: () => ({}) },
	},
	computed: {
		cropable() { return isMediaLayer(this.layer); },
		insets() { return cropInsets(this.layer); },
	},
	methods: {
		round(n) { return Math.round(n); },
		center(axis) {
			if(axis === 'x') this.layer.x = Math.round((this.overlay.width - this.layer.w) / 2);
			else this.layer.y = Math.round((this.overlay.height - this.layer.h) / 2);
			this.$emit('save');
		},
		fill() {
			Object.assign(this.layer, { x: 0, y: 0, w: this.overlay.width, h: this.overlay.height, rotation: 0 });
			this.$emit('save');
		},
		// Typing an exact crop inset has the same effect as dragging that edge's handle to match: the box's
		// geometry changes right alongside the crop fraction, and the opposite edge doesn't move.
		setInset(side, value) {
			const n = parseFloat(value);
			if(!Number.isFinite(n)) return;
			const { x, y, w, h, crop } = setCropInset(this.layer, side, n);
			Object.assign(this.layer, { x, y, w, h });
			if(crop) this.$set(this.layer.props, 'crop', crop);
			else this.$delete(this.layer.props, 'crop');
			this.$emit('save');
		},
		// Goes back to the file's own resolution (not just whatever it was before a crop), around the same centre;
		// a crop no longer means anything once the size it was relative to is gone, so it's cleared too.
		resetSize() {
			const media = this.mediaSizes[this.layer.props?.src];
			const { w, h } = media ? { w: media[0], h: media[1] } : naturalSize(this.layer);
			const cx = this.layer.x + this.layer.w / 2, cy = this.layer.y + this.layer.h / 2;
			this.layer.w = Math.round(w);
			this.layer.h = Math.round(h);
			this.layer.x = Math.round(cx - w / 2);
			this.layer.y = Math.round(cy - h / 2);
			this.$delete(this.layer.props, 'crop');
			this.$emit('save');
		},
		resetRotation() {
			this.layer.rotation = 0;
			this.$emit('save');
		},
		// Goes back to the box's size from before this crop, around the same centre, rather than leaving a small
		// box that would stretch the whole image back out to fill it.
		resetCrop() {
			if(!this.layer.props?.crop) return;
			const { w, h } = naturalSize(this.layer);
			const cx = this.layer.x + this.layer.w / 2, cy = this.layer.y + this.layer.h / 2;
			this.layer.w = Math.round(w);
			this.layer.h = Math.round(h);
			this.layer.x = Math.round(cx - w / 2);
			this.layer.y = Math.round(cy - h / 2);
			this.$delete(this.layer.props, 'crop');
			this.$emit('save');
		},
	},
};
</script>
